import React, { useCallback, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { Settings, Preview, CheckCircle, Check } from "@mui/icons-material";
import { themes } from "services/constants";
import { AnimatedConnector } from "components/CustomComponents/Stepper";
import { BulkImportConfigure } from "./BulkImportConfigure";
import { BulkImportPreview } from "./BulkImportPreview";
import { BulkImportReview } from "./BulkImportReview";

const steps = [
  { label: "Configure", icon: 1 },
  { label: "Preview", icon: 2 },
  { label: "Result", icon: 3 },
];

function CustomBulkImportStepIcon(props) {
  const { active, completed, icon } = props;

  const icons = {
    1: <Settings />,
    2: <Preview />,
    3: <CheckCircle />,
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 40,
        height: 40,
        borderRadius: "50%",
        bgcolor: completed ? "#00D4AA" : active ? "#00D4AA" : "rgba(0,0,0,0.1)",
        color: "white",
      }}
    >
      {completed ? <Check /> : icons[String(icon)]}
    </Box>
  );
}

export const BulkImport = ({
  setShowBulkImportDialog,
  payScheduleData,
  payScheduleId,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    payroll: payScheduleData
      ? {
          label: payScheduleData.name,
          value: payScheduleData.id || payScheduleId,
        }
      : null,
    componentType: null,
    componentName: null,
    csvFile: null,
    fileName: "",
    fileSize: "",
    previewData: [],
  });

  const handleNext = useCallback(() => {
    setActiveStep((currentStep) =>
      currentStep < steps.length - 1 ? currentStep + 1 : currentStep,
    );
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((currentStep) =>
      currentStep > 0 ? currentStep - 1 : currentStep,
    );
  }, []);

  const handleDataUpdate = useCallback((data) => {
    setFormData((prev) => ({
      ...prev,
      ...data,
    }));
  }, []);

  const renderStepContent = (activeStep) => {
    switch (activeStep) {
      case 0:
        return (
          <BulkImportConfigure
            data={formData}
            onUpdate={handleDataUpdate}
            onNext={handleNext}
            onClose={() => setShowBulkImportDialog(false)}
            payScheduleId={payScheduleId}
          />
        );
      case 1:
        return (
          <BulkImportPreview
            data={formData}
            onUpdate={handleDataUpdate}
            onNext={handleNext}
            onBack={handleBack}
            payScheduleId={payScheduleId}
          />
        );
      case 2:
        return (
          <BulkImportReview
            data={formData}
            onBack={handleBack}
            onClose={() => setShowBulkImportDialog(false)}
            payScheduleId={payScheduleId}
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FB", pb: 4 }}>
      <Box sx={{ px: { xs: 2, md: 2 }, pt: 3, maxWidth: 980, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: themes?.whiteColor,
            border: `1px solid ${themes?.miliedLightGray}`,
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Import Payroll Data
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 620 }}
          >
            Bulk-add earnings or deductions into the current payroll run via
            CSV.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              backgroundColor: "rgba(0, 212, 170, 0.08)",
              border: `1px solid rgba(0, 212, 170, 0.18)`,
            }}
          >
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              connector={<AnimatedConnector />}
            >
              {steps.map((step) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={CustomBulkImportStepIcon}
                    sx={{
                      "& .MuiStepLabel-label.Mui-active": {
                        color: "#00D4AA",
                        fontWeight: "bold",
                      },
                      "& .MuiStepLabel-label.Mui-completed": {
                        color: "#00D4AA",
                      },
                    }}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        </Paper>

        <Box sx={{ minHeight: "calc(100vh - 260px)", mt: 0 }}>
          {renderStepContent(activeStep)}
        </Box>
      </Box>
    </Box>
  );
};
